<template>
  <!-- 🔴 `v-if`, never `v-show`: a row whose kind has no card must leave NOTHING in the DOM.
       The table renders every item of every section, so a hidden-but-present node per row is a
       node per row that the browser still lays out — and it would also mount a panel that owns a
       debounce timer and a watcher for a card that can never exist. -->
  <div
    v-if="hasCard"
    ref="root"
    class="tq2-inline-preview"
    :data-testid="'tq2-inline-preview' + testidSuffix"
  >
    <TaxCardPreviewPanelV2
      :item="item"
      :active="enabled && visible"
      compact
      :testid-suffix="testidSuffix"
    />
  </div>
</template>

<script>
// The card preview as it appears INSIDE a question-table row.
//
// A thin wrapper: `TaxCardPreviewPanelV2` still does all the rendering (debounce, request ticket,
// "an error never clears the last good card"), and this file only decides the two things the table
// adds to that — WHETHER this row has a card at all, and WHEN its panel is allowed to talk to the
// server.
//
// ── Why the laziness is not an optimisation ─────────────────────────────────────────────────────
//
// One card is one `POST card/preview`. There is no batch endpoint, and no cache shared between
// panels — two rows with byte-identical configs still cost two round-trips. A browser opens about
// six connections per origin, so a version with fifty card rows that all fire on the switch queues
// forty-four of them behind the first six: the author flips the switch to COMPARE cards and the
// ones further down the page stay blank for many seconds while requests they will never look at
// hold the pipe. Rendering only what has reached the viewport (plus `ROOT_MARGIN` of runway, so the
// card is already there by the time it is scrolled to) keeps the in-flight set to what fits on a
// screen, whatever the version's size.
//
// 🔴 STRICTLY READ-ONLY ON `item` — it is the store's live row, and the view's dirty-tracking
// (`tqContentSig()` / `snapshotBaseline()`) reads those rows. Anything this component wrote onto
// one would register as an unsaved edit; all of its own state lives here instead.
import TaxCardPreviewPanelV2 from './TaxCardPreviewPanelV2.vue';
import { hasCardPreview } from '../utils/taxCardPreview';

/**
 * How much runway below the fold counts as "on screen". Renders the next card or so ahead of the
 * scroll, which is what makes the laziness invisible to the reader rather than a visible pop-in.
 */
const ROOT_MARGIN = '200px';

export default {
  name: 'TaxCardInlinePreviewV2',
  components: { TaxCardPreviewPanelV2 },
  props: {
    /** The table's row, as the store holds it. READ-ONLY — see the file header. */
    item: { type: Object, required: true },
    /**
     * The page-wide "แสดงตัวอย่างการ์ด" switch. `false` is a hard off: no observer, no visible
     * card, and — because `active` is gated on it too — no request in flight or about to be.
     */
    enabled: { type: Boolean, default: false },
    /**
     * Appended to this wrapper's and the panel's `data-testid`s, VERBATIM: the caller supplies its
     * own separator (`'-539'`), because it also chooses what identifies an unsaved row.
     */
    testidSuffix: { type: String, default: '' },
  },
  data() {
    return {
      /**
       * Has this row ever reached the viewport while enabled? One-way while the switch is on: a
       * card scrolled back out stays rendered, so scrolling up and down a long version does not
       * re-ask the server for cards it already drew.
       */
      visible: false,
    };
  },
  computed: {
    /**
     * 🔴 Delegated to `utils/taxCardPreview.js` and NEVER re-listed here. The kind list written out
     * a second time is the one that drifts — the dialog carries the same note over its own gate.
     */
    hasCard() {
      return hasCardPreview(this.item && this.item.itemKind);
    },
  },
  watch: {
    // Not `immediate`: the initial arming belongs to `mounted()`, where the root element provably
    // exists. Here we only handle the switch being flipped.
    enabled(on) {
      if (on) this.arm();
      else this.reset();
    },
    // An item that becomes (or stops being) a card kind while the page is open — the row's own
    // node appears/disappears with it, so the observer has to follow.
    hasCard(has) {
      if (has) this.arm();
      else this.reset();
    },
  },
  mounted() {
    /**
     * 🔴 Deliberately NOT in `data()`. `reactive()` would wrap the observer in a Proxy, and a
     * native DOM method called through one runs with the proxy as its `this` — `disconnect()` then
     * throws "Illegal invocation" and every observer on the page leaks. This is plain instance
     * state; nothing renders from it.
     */
    this.observer = null;
    if (this.enabled) this.arm();
  },
  beforeUnmount() {
    this.stopObserving();
  },
  methods: {
    /**
     * Start watching for this row to reach the viewport, if there is anything left to watch for.
     * Idempotent, and a no-op once `visible` is set — the reveal is one-way.
     */
    arm() {
      if (!this.enabled || !this.hasCard || this.visible || this.observer) return;
      // No `IntersectionObserver` (old browser, or a test environment with a partial DOM) must not
      // mean "no preview": fall back to rendering the card straight away. Worse for a huge version,
      // still correct, and the feature is off by default anyway.
      if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
        this.visible = true;
        return;
      }
      // `hasCard` may have flipped in this same tick, so the node exists only after the re-render.
      this.$nextTick(() => {
        if (!this.enabled || !this.hasCard || this.visible || this.observer) return;
        const el = this.$refs.root;
        if (!el) return;
        const observer = new window.IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.visible = true;
            // Unobserve on the FIRST hit: the panel is now allowed to render for good, and
            // re-entering the viewport must not look like a reason to render again.
            this.stopObserving();
          },
          { rootMargin: ROOT_MARGIN },
        );
        observer.observe(el);
        this.observer = observer;
      });
    },
    /** Drop the observer, if any. Leaves `visible` alone. Idempotent. */
    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    /**
     * Back to the state before the switch was on: no observer, and no card — so flipping it off
     * leaves neither a stale card on screen nor a panel that could still fire a request.
     */
    reset() {
      this.stopObserving();
      this.visible = false;
    },
  },
};
</script>

<style scoped>
/* 🔴 NO height / max-height / overflow here. A scroller nested inside the table cell would trap
   the wheel over every card and clip the card's own footer; the page is the only scroller. Width is
   the host cell's business too — the table column applies the `width: 0; min-width: 100%` trick that
   keeps an auto-layout column from being widened by its content, and a width set here would fight
   it. All this rule does is keep the panel from imposing a min-content width of its own. */
.tq2-inline-preview {
  min-width: 0;
}
</style>
